import * as React from "react";
import { observer, inject } from "mobx-react";
import { Viewport, ViewSmall, ViewMedium } from "bernie-view";

import {
  UitkLayoutGrid,
  UitkLayoutGridItem,
  UitkLayoutConditionalGridColumnRow,
  UitkLayoutConditionalGridSpan,
} from "uitk-react-layout-grid";

import { Location } from "components/shared/Location/Location";
import { Dates } from "components/shared/Dates/Dates";
import { Travelers } from "components/shared/Travelers/Travelers";

import { LobErrorSummary } from "components/shared/LobErrorSummary/LobErrorSummary";

import { HotelHiddenSearchData } from "../components/HotelHiddenSearchData";

import { NoRehydratationValues } from "components/shared/TravelersField/typings";
import { WizardHotelPWAv2Props } from "../typings";
import { locKeys } from "components/flexComponents/WizardHotelPWA/components/l10n";
import { useLocalization } from "@shared-ui/localization-context";
import { RateCalendarFooter } from "components/flexComponents/WizardHotelPWA/components/RateCalendarFooter";
import { CheckboxModuleNameTypes } from "src/components/shared/PropertyFIlterComponents/components/Checkbox";

import { UitkSpacing } from "uitk-react-spacing";
import { UitkHeading } from "uitk-react-text";
import { classNames } from "uitk-react-utils";
import { RateCalendarStore } from "src/stores/rateCalendar";
import { SubmitButton } from "src/components/shared/SubmitButton/SubmitButton";

enum DateState {
  START = "startDate",
  END = "endDate",
}

function usePreviousValue(value: boolean) {
  const ref = React.useRef<boolean>(false);

  React.useEffect(() => {
    ref.current = value;
  }, []);

  return ref.current;
}

export const WizardHotelPWAv2RowView: React.FC<WizardHotelPWAv2Props> = observer((props) => {
  const { context, wizardState, analytics, flexTemplateNode, compositionStore } = props;
  const isDesktop = !context?.deviceInformation?.mobile && !context?.deviceInformation?.tablet;

  const [wizardContainerIsOutOfWindow, setWizardContainerIsOutOfWindow] = React.useState(false);
  const [datePickerIsOpen, setDatePickerIsOpen] = React.useState(
    Boolean(isDesktop && wizardState.hotelWizardState.config.desktopAutoOpenCalendar)
  );

  const wizardRef = React.useRef<HTMLDivElement>(null);
  const wizardSmallViewContainer = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    window.addEventListener("scroll", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const previousWizardContainerIsOutOfWindow = usePreviousValue(wizardContainerIsOutOfWindow);

  React.useEffect(() => {
    // this prevent the new date picker to stay opened when switching between sticky/non-sticky wizard
    if (previousWizardContainerIsOutOfWindow !== wizardContainerIsOutOfWindow) {
      setDatePickerIsOpen(false);
    }
  }, [wizardContainerIsOutOfWindow]);

  const onScroll = React.useCallback(() => {
    if (wizardRef && wizardRef.current) {
      const currentWizardRef = wizardRef.current.getBoundingClientRect().bottom;
      setWizardContainerIsOutOfWindow(currentWizardRef < 0);
    }
  }, [wizardRef, setWizardContainerIsOutOfWindow]);

  const toggleDatePicker = () => {
    setDatePickerIsOpen(!datePickerIsOpen);
  };

  const heading = compositionStore?.composition?.pageHeading;
  const subHeading = compositionStore?.composition?.pageSubHeadline;
  const isMobile = context.deviceInformation?.mobile;
  const isSticky = wizardContainerIsOutOfWindow && !isMobile;
  const wizardFormProps = {
    flexTemplateNode,
    wizardState,
    analytics,
    context,
    isSticky,
    toggleDatePicker,
    datePickerIsOpen,
    isDesktop,
  };

  return (
    <div className="WizardHotelPWAv2Row WizardHotelPWA" ref={wizardRef}>
      {heading && (
        <UitkHeading tag="h1" size={3} className="is-visually-hidden">
          {heading}
        </UitkHeading>
      )}
      {subHeading && (
        <UitkHeading tag="h2" size={5} className="is-visually-hidden">
          {subHeading}
        </UitkHeading>
      )}
      <Viewport>
        <ViewSmall>
          <div ref={wizardSmallViewContainer}>
            <WizardForm {...wizardFormProps} isMobile />
          </div>
        </ViewSmall>
        <ViewMedium>
          <WizardForm {...wizardFormProps} isMobile={false} />
        </ViewMedium>
      </Viewport>
    </div>
  );
});

interface WizardFormProps
  extends Pick<WizardHotelPWAv2Props, "flexTemplateNode" | "wizardState" | "analytics" | "context"> {
  isMobile: boolean;
  isSticky: boolean;
  toggleDatePicker: () => void;
  datePickerIsOpen: boolean;
  rateCalendar?: RateCalendarStore;
  isDesktop: boolean;
}

const WizardForm: React.FC<WizardFormProps> = inject("rateCalendar")(
  observer((props) => {
    const {
      context,
      wizardState,
      flexTemplateNode,
      isMobile,
      isSticky,
      toggleDatePicker,
      datePickerIsOpen,
      rateCalendar,
      isDesktop,
    } = props;
    const {
      globalWizardState,
      hotelWizardState,
      hotelWizardState: { config, numberOfErrors, errorInputRef, moreThanOneError },
    } = wizardState;

    const { elementId } = config;

    const { formatText, formatDateString } = useLocalization();

    const { options } = flexTemplateNode;

    const mainTitleWizardClassNames: UitkLayoutConditionalGridSpan = { medium: 12, large: 12 };
    const cardColSpan: UitkLayoutConditionalGridSpan = { medium: 4, large: 12 };
    const mainGridColumns: UitkLayoutConditionalGridColumnRow = { medium: 4, large: 8 };
    const locationColSpan: UitkLayoutConditionalGridSpan = { medium: 4, large: 2 };
    const datesColSpan: UitkLayoutConditionalGridSpan = { medium: 2, large: 3 };
    const travelersColSpan: UitkLayoutConditionalGridSpan = { medium: 1, large: 2 };
    const submitColSpan: UitkLayoutConditionalGridSpan = { medium: 1, large: 1 };

    const [htgStartInvalidMessage, setStartInvalidMessage] = React.useState<string>("");
    const [htgEndInvalidMessage, setEndInvalidMessage] = React.useState<string>("");

    if (!hotelWizardState.regionId) {
      hotelWizardState.setRegionId(context.searchContext.location.id);
    }

    if (!isDesktop) {
      const dateFormat = hotelWizardState.config.date.format;
      hotelWizardState.setHtgStartDate(formatDateString(hotelWizardState.date.start, { raw: dateFormat }));
      hotelWizardState.setHtgEndDate(formatDateString(hotelWizardState.date.end, { raw: dateFormat }));
    }

    /**
     * Values used if no rehydratation(JS disabled)
     */
    const noRehydratationValues: NoRehydratationValues = {
      numOfTravelers: 2,
      name: "adults",
    };

    const setInputsRefs = (ref: React.RefObject<HTMLInputElement>, secondRef?: React.RefObject<HTMLInputElement>) => {
      hotelWizardState.wizardInputsArray.push(ref);
      if (secondRef) {
        hotelWizardState.wizardInputsArray.push(secondRef);
      }
    };

    const getLocError = (isLink: boolean) => {
      if (!isLink) {
        return hotelWizardState.moreThanOneError()
          ? config.form.submitButton.locMultipleWarningsToken
          : config.form.submitButton.locWarningToken;
      }

      return hotelWizardState.moreThanOneError()
        ? config.form.submitButton.locMultipleWarningsLinkToken
        : config.form.submitButton.locWarningLinkToken;
    };

    const isValidDate = (date: Date) => {
      return date && date.toString() !== "Invalid Date";
    };

    const validate = (key: string) => {
      const checkInDate = new Date(hotelWizardState.startDate);
      const checkOutDate = new Date(hotelWizardState.endDate);

      if (key === DateState.START && !isValidDate(checkInDate)) {
        return formatText(locKeys.invalidFromDate);
      }

      if (key === DateState.END && !isValidDate(checkOutDate)) {
        return formatText(locKeys.invalidToDate);
      }

      if (key === DateState.END && isValidDate(checkInDate)) {
        const maxEndDate = new Date(checkInDate.getFullYear(), checkInDate.getMonth(), checkInDate.getDate() + 28);

        if (checkOutDate < checkInDate) {
          return formatText(locKeys.invalidDateRangeEndBeforeStart);
        }

        if (checkOutDate > maxEndDate) {
          return formatText(locKeys.invalidDateRangeEndOverLimit);
        }
      }

      return "";
    };

    const validateAndGetFormErrors = (event?: React.FormEvent<HTMLFormElement>) => {
      const errors = [];
      const startDateError = validate(DateState.START);
      const endDateError = validate(DateState.END);

      setStartInvalidMessage(startDateError);
      setEndInvalidMessage(endDateError);

      if (startDateError) {
        errors.push(startDateError);
      }

      if (endDateError) {
        errors.push(endDateError);
      }

      if (errors.length > 0 && event && typeof event.preventDefault === "function") {
        event.preventDefault();
      }

      return errors;
    };

    const onSubmitForm = (e: React.FormEvent<HTMLFormElement>) => {
      if (validateAndGetFormErrors().length > 0) {
        e.preventDefault();
      }
    };

    const submitForm = () => {
      if (validateAndGetFormErrors().length === 0) {
        wizardFormRef?.current?.submit();
      }
    };

    const wizardFormRef = React.useRef<HTMLFormElement>(null);

    const mainWizardContainerClassNames = classNames({
      sticky: isSticky,
    });
    const wizardFormTestId = isSticky ? "wizard-sticky" : "wizard";

    /* istanbul ignore next */
    const rateCalendarProps = rateCalendar?.isExperimentEnabled
      ? {
          datePickerContent: rateCalendar.datePickerContent,
          footerHtml: <RateCalendarFooter {...rateCalendar.footerHtmlProps} />,
          footerText: rateCalendar.footerText,
        }
      : {};

    return (
      <UitkLayoutGrid columns={mainGridColumns} className={mainWizardContainerClassNames}>
        <UitkLayoutGridItem colSpan={cardColSpan}>
          <UitkSpacing padding={isSticky ? { blockend: "three" } : { block: "six" }}>
            <form
              noValidate
              id={elementId}
              data-fm={options.fmId}
              data-fm-title-id={options.fmTitleId}
              onSubmit={onSubmitForm}
              action={hotelWizardState.formAction}
              data-testid={wizardFormTestId}
              ref={wizardFormRef}
            >
              <UitkLayoutGrid columns={mainGridColumns} space="three">
                {numberOfErrors > 0 && (
                  <LobErrorSummary
                    locHeadingToken={getLocError(false)!}
                    locHeadingArgs={moreThanOneError() && [numberOfErrors]}
                    locLinkToken={getLocError(true)}
                    linkClickFocusId="roundtrip-error"
                    inputErrorRef={errorInputRef}
                    colSpan={mainGridColumns}
                    setInputsRefs={setInputsRefs}
                  />
                )}
                {isSticky && !isMobile && (
                  <UitkLayoutGridItem colSpan={mainTitleWizardClassNames}>
                    <h1 className="uitk-type-500 is-visually-hidden">{formatText(locKeys.stickyWizardTitle)}</h1>
                  </UitkLayoutGridItem>
                )}
                <Location
                  originField={false}
                  destinationField
                  lobState={hotelWizardState}
                  colSpan={locationColSpan}
                  setInputsRefs={setInputsRefs}
                  globalConfig={globalWizardState.config}
                />
                <Dates
                  lobState={hotelWizardState}
                  colSpan={datesColSpan}
                  setInputsRefs={setInputsRefs}
                  autoOpenCalendar={datePickerIsOpen}
                  toggleDatePicker={toggleDatePicker}
                  isDesktop={isDesktop}
                  {...rateCalendarProps}
                  htgStartInvalidMessage={htgStartInvalidMessage}
                  htgEndInvalidMessage={htgEndInvalidMessage}
                  validateAndGetFormErrors={validateAndGetFormErrors}
                  submitForm={submitForm}
                />

                <Travelers
                  lobState={hotelWizardState}
                  colSpan={travelersColSpan}
                  noRehydratationValues={noRehydratationValues}
                  submitForm={submitForm}
                />

                <SubmitButton
                  lobState={hotelWizardState}
                  colSpan={submitColSpan}
                  rfrr="SearchClick_Hotel"
                  globalConfig={globalWizardState.config}
                  moduleName={CheckboxModuleNameTypes.WIZARD_HOTEL_V2_ROW}
                  testId="wizard-submit-button"
                />
              </UitkLayoutGrid>
              <HotelHiddenSearchData hotelWizardState={hotelWizardState} />
            </form>
          </UitkSpacing>
        </UitkLayoutGridItem>
      </UitkLayoutGrid>
    );
  })
);

export default WizardHotelPWAv2RowView;
